import React from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAppDispatch } from "../../../common/hooks/hooks";
import { closeGlobalModal } from "../../../common/features/globalModal/globalModalSlice";
import { CouponSummary } from "../types/userAuthApiTypes";
import Icon from "../../../common/components/Icon/Icon";

interface WelcomeCouponsModalProps {
  coupons?: CouponSummary[];
}

const WelcomeCouponsModal: React.FC<WelcomeCouponsModalProps> = ({
  coupons,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success(t("user.coupons.copied", "Coupon code copied!"));
  };

  const handleClose = () => {
    dispatch(closeGlobalModal());
    // Redirect to home or shop after closing the welcome modal
    window.location.href = window.commonVariables?.shopUrl || "/";
  };

  if (!coupons || coupons.length === 0) {
    return null;
  }

  return (
    <div className="welcome-coupons-modal">
      <div className="welcome-coupons-modal__header">
        <div className="welcome-coupons-modal__icon-wrapper">
          <Icon iconName="gift-outlined" className="icon--stroke-thick" />
        </div>
        <h3 className="welcome-coupons-modal__title">
          {t("user.welcomeCoupons.title", "Welcome to the Family!")}
        </h3>
        <p className="welcome-coupons-modal__subtitle">
          {t(
            "user.welcomeCoupons.subtitle",
            "Here are some exclusive gifts for your first orders.",
          )}
        </p>
      </div>

      <div className="welcome-coupons-modal__list">
        {coupons.map((coupon, index) => (
          <div key={index} className="welcome-coupon-card">
            <div className="welcome-coupon-card__left">
              <span className="welcome-coupon-card__amount">
                {coupon.discountType === "percent"
                  ? `${Math.round(Number(coupon.amount))}%`
                  : `₺${Math.round(Number(coupon.amount))}`}
              </span>
              <span className="welcome-coupon-card__label">
                {coupon.discountType === "percent"
                  ? t("user.coupons.off", "OFF")
                  : t("user.coupons.discount", "Discount")}
              </span>
            </div>
            <div className="welcome-coupon-card__right">
              <p className="welcome-coupon-card__desc">{coupon.description}</p>
              <div
                className="welcome-coupon-card__code-box"
                onClick={() => handleCopy(coupon.code)}
                role="button"
                tabIndex={0}
              >
                <code>{coupon.code}</code>
                <Icon iconName="copy" className="copy-icon" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="welcome-coupons-modal__disclaimer">
        {t("user.welcomeCoupons.disclaimer")}
      </p>

      <button
        onClick={handleClose}
        className="button button--primary welcome-coupons-modal__cta"
      >
        {t("user.welcomeCoupons.startShopping", "Start Shopping")}
      </button>
    </div>
  );
};

export default WelcomeCouponsModal;
